import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../lib/LanguageContext';
import { Search, Menu, User, Heart, Bookmark, Clock, LogOut, ChevronDown, Film, Shield } from '../common/Icons';
import LanguageSelector from '../common/LanguageSelector';
import AdBanner from '../common/AdBanner';

const GENRES = [
  { id: 28, name: 'Action', slug: 'action' },
  { id: 16, name: 'Animation', slug: 'animation' },
  { id: 35, name: 'Comedy', slug: 'comedy' },
  { id: 80, name: 'Crime', slug: 'crime' },
  { id: 99, name: 'Documentary', slug: 'documentary' },
  { id: 18, name: 'Drama', slug: 'drama' },
  { id: 10751, name: 'Family', slug: 'family' },
  { id: 14, name: 'Fantasy', slug: 'fantasy' },
  { id: 36, name: 'History', slug: 'history' },
  { id: 27, name: 'Horror', slug: 'horror' },
  { id: 10402, name: 'Music', slug: 'music' },
  { id: 9648, name: 'Mystery', slug: 'mystery' },
  { id: 10749, name: 'Romance', slug: 'romance' },
  { id: 878, name: 'Sci-Fi', slug: 'sci-fi' },
  { id: 53, name: 'Thriller', slug: 'thriller' },
  { id: 10752, name: 'War', slug: 'war' },
  { id: 37, name: 'Western', slug: 'western' },
];

interface NavbarProps {
  onMenuToggle: () => void;
}

export default function Navbar({ onMenuToggle }: NavbarProps) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [genresOpen, setGenresOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const genresRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (genresRef.current && !genresRef.current.contains(e.target as Node)) setGenresOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setGenresOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-surface-950/95 backdrop-blur-md shadow-lg border-b border-surface-800/50' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
        <button onClick={onMenuToggle} className="lg:hidden p-2 -ml-2 text-surface-400 hover:text-white">
          <Menu />
        </button>

        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <Film className="text-primary" size={28} />
          <span className="text-xl font-bold hidden sm:block">
            <span className="text-primary">Cine</span>Verse
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 ml-6">
          <Link to="/" className="text-sm font-medium text-surface-300 hover:text-white transition-colors">
            {t('home')}
          </Link>
          <Link to="/movies/popular" className="text-sm font-medium text-surface-300 hover:text-white transition-colors">
            {t('movies')}
          </Link>
          <Link to="/tvshows/popular" className="text-sm font-medium text-surface-300 hover:text-white transition-colors">
            {t('tvShows')}
          </Link>
          <div ref={genresRef} className="relative">
            <button
              onClick={() => setGenresOpen(!genresOpen)}
              className="flex items-center gap-1 text-sm font-medium text-surface-300 hover:text-white transition-colors"
            >
              {t('genres')} <ChevronDown size={14} className={`transition-transform ${genresOpen ? 'rotate-180' : ''}`} />
            </button>
            {genresOpen && (
              <div className="absolute top-full left-0 mt-2 w-[480px] bg-surface-900 border border-surface-800 rounded-xl shadow-2xl p-4 animate-slide-up">
                <div className="grid grid-cols-3 gap-2">
                  {GENRES.map((genre) => (
                    <Link
                      key={genre.id}
                      to={`/genre/movie/${genre.slug}`}
                      onClick={() => setGenresOpen(false)}
                      className="px-3 py-2 text-sm text-surface-300 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
                    >
                      {genre.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-surface-800">
                  <p className="text-xs font-medium text-surface-500 mb-2">TV Genres</p>
                  <div className="grid grid-cols-3 gap-2">
                    {GENRES.slice(0, 6).map((genre) => (
                      <Link
                        key={genre.id}
                        to={`/genre/tv/${genre.slug}`}
                        onClick={() => setGenresOpen(false)}
                        className="px-3 py-2 text-sm text-surface-300 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
                      >
                        {genre.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <Link to="/articles" className="text-sm font-medium text-surface-300 hover:text-white transition-colors">
            {t('articles')}
          </Link>
          <Link to="/calendar" className="text-sm font-medium text-surface-300 hover:text-white transition-colors">
            {t('calendar')}
          </Link>
        </div>

        <div className="flex-1" />

        <form onSubmit={handleSearch} className="hidden sm:flex items-center">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
            <input
              type="text"
              placeholder={t('searchMoviesTvShows')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-52 lg:w-64 pl-9 pr-4 py-2 bg-surface-800/50 border border-surface-700 rounded-lg text-sm text-white placeholder-surface-500 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/25 transition-all"
            />
          </div>
        </form>

        <Link
          to="/search"
          className="sm:hidden p-2 text-surface-400 hover:text-white"
        >
          <Search size={20} />
        </Link>

        <div className="flex items-center gap-2">
          <LanguageSelector />
          <div ref={userRef} className="relative">
            {user ? (
            <button
              onClick={() => setUserOpen(!userOpen)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-surface-800 transition-colors"
            >
              {user.avatar ? (
                <img src={user.avatar_url || ''} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <User size={16} className="text-primary" />
                </div>
              )}
              <span className="hidden md:block text-sm font-medium max-w-[100px] truncate">{user.name}</span>
            </button>
          ) : (
            <Link
              to="/login"
              className="px-4 py-2 bg-primary hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {t('signIn')}
            </Link>
          )}

          {userOpen && user && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-surface-900 border border-surface-800 rounded-xl shadow-2xl overflow-hidden animate-slide-up">
              <div className="px-4 py-3 border-b border-surface-800">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-surface-500">{user.email}</p>
              </div>
              <div className="py-1">
                <Link
                  to="/favorites"
                  onClick={() => setUserOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
                >
                  <Heart size={16} /> {t('favorites')}
                </Link>
                <Link
                  to="/watchlist"
                  onClick={() => setUserOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
                >
                  <Bookmark size={16} /> {t('watchlist')}
                </Link>
                <Link
                  to="/history"
                  onClick={() => setUserOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
                >
                  <Clock size={16} /> {t('watchHistory')}
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setUserOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
                  >
                    <Shield size={16} /> Admin Dashboard
                  </Link>
                )}
              </div>
              <div className="border-t border-surface-800 py-1">
                <button
                  onClick={() => {
                    logout();
                    setUserOpen(false);
                    navigate('/');
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-surface-800 transition-colors"
                >
                  <LogOut size={16} /> {t('signOut')}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 pb-2 hidden md:flex justify-center">
        <AdBanner placement="header" />
      </div>
    </nav>
  );
}
